import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { X } from 'lucide-react';

import { LAYER_NORM_CUSTOM, LAYER_NORM_NONE } from '../../../../constants/layers.ts';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { setNormPrompt, updatePlane } from '../../../../store/reducers/measurementPlanes.reducer.ts';
import { getNorms } from '../../../../store/selectors/user.selector.ts';
import { NormChipGrid } from './NormChipGrid.tsx';

const W = 280; // popup width
const H = 196; // approx popup height (title + hint + chip grid)
const HEADER = 72; // keep clear of the app header
const MARGIN = 8;

/** Floating norm picker shown right where the user finished drawing a surface. Picking a
 *  norm sets it on the surface (which triggers the calculation); the result then appears
 *  in the sidebar. Norms already used on the project's layers are outlined as suggestions.
 *  Always kept fully on-screen. Esc / × dismiss it (the surface keeps "No norm"). */
export function NormPromptPopup(): JSX.Element | null {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const prompt = useAppSelector((s) => s.measurementPlanes.normPrompt);
  const modelLayers = useAppSelector((s) => s.workspace.modelLayers);
  const norms = useSelector(getNorms);
  const suggested = useMemo(
    () => [
      ...new Set(
        modelLayers
          .map((l) => l.selectedNorm)
          .filter((n): n is string => !!n && n !== LAYER_NORM_NONE && n !== LAYER_NORM_CUSTOM),
      ),
    ],
    [modelLayers],
  );

  useEffect(() => {
    if (!prompt) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dispatch(setNormPrompt(null));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prompt, dispatch]);

  if (!prompt) return null;

  const pick = (id: number | null, name: string | null) => {
    dispatch(updatePlane({ id: prompt.planeId, changes: { normId: id, normName: name } }));
    dispatch(setNormPrompt(null));
  };

  // Anchor above the cursor; flip below if it would clip the header, then clamp on-screen.
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let top = prompt.y - 14 - H;
  if (top < HEADER) top = prompt.y + 16;
  top = Math.max(HEADER, Math.min(top, vh - H - MARGIN));
  const left = Math.max(MARGIN, Math.min(prompt.x - W / 2, vw - W - MARGIN));

  return (
    <div
      style={{
        position: 'fixed',
        left,
        top,
        width: W,
        zIndex: 60,
        background: 'rgba(18, 18, 20, 0.95)',
        backdropFilter: 'blur(18px) saturate(160%)',
        WebkitBackdropFilter: 'blur(18px) saturate(160%)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 12,
        boxShadow: '0 12px 36px rgba(0,0,0,0.55)',
        padding: 10,
        pointerEvents: 'auto',
        userSelect: 'none',
      }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#d4ff33' }}>
          {t('workspace.panel.surfaces.selectNorm')}
        </span>
        <button
          onClick={() => dispatch(setNormPrompt(null))}
          style={{ color: 'rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center' }}
          title={t('common.close')}
          aria-label={t('common.close')}
        >
          <X size={14} />
        </button>
      </div>
      <p className="mb-2 text-[11px] leading-snug text-white/60">{t('workspace.panel.surfaces.normHint')}</p>
      <NormChipGrid norms={norms} value={null} suggested={suggested} onPick={pick} />
    </div>
  );
}
